<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import DeleteDialog from 'dashboard/components-next/aidexacx/pageComponents/DeleteDialog.vue';
import PageLayout from 'dashboard/components-next/aidexacx/PageLayout.vue';
import ConnectInboxDialog from 'dashboard/components-next/aidexacx/pageComponents/inbox/ConnectInboxDialog.vue';
import InboxCard from 'dashboard/components-next/aidexacx/assistant/InboxCard.vue';
import InboxPageEmptyState from 'dashboard/components-next/aidexacx/pageComponents/emptyStates/InboxPageEmptyState.vue';

const store = useStore();
const dialogType = ref('');
const route = useRoute();
const { t } = useI18n();

const assistantId = computed(() => route.params.assistantId);
const assistantUiFlags = useMapGetter('aidexacxAssistants/getUIFlags');
const uiFlags = useMapGetter('aidexacxInboxes/getUIFlags');
const isFetchingAssistant = computed(() => assistantUiFlags.value.fetchingItem);
const isFetching = computed(() => uiFlags.value.fetchingList);

const aidexacxInboxes = useMapGetter('aidexacxInboxes/getRecords');

const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);

const handleDelete = () => {
  disconnectInboxDialog.value.dialogRef.open();
};

const connectInboxDialog = ref(null);

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value.dialogRef.open());
};
const handleAction = async ({ action, id, value }) => {
  selectedInbox.value = aidexacxInboxes.value.find(inbox => id === inbox.id);
  if (action === 'delete') {
    nextTick(() => handleDelete());
  } else if (action === 'toggleAutoReply') {
    try {
      await store.dispatch('aidexacxInboxes/update', {
        assistantId: assistantId.value,
        inboxId: id,
        autoReply: value,
      });
      useAlert(
        value
          ? t('AIDEXACX.INBOXES.AUTO_REPLY.ENABLE_SUCCESS')
          : t('AIDEXACX.INBOXES.AUTO_REPLY.DISABLE_SUCCESS')
      );
    } catch {
      useAlert(t('AIDEXACX.INBOXES.AUTO_REPLY.ERROR'));
    }
  }
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedInbox.value = null;
};

onMounted(() =>
  store.dispatch('aidexacxInboxes/get', {
    assistantId: assistantId.value,
  })
);
</script>

<template>
  <PageLayout
    :header-title="$t('AIDEXACX.INBOXES.HEADER')"
    :button-label="$t('AIDEXACX.INBOXES.ADD_NEW')"
    :button-policy="['administrator']"
    :is-fetching="isFetchingAssistant || isFetching"
    :is-empty="!aidexacxInboxes.length"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.AIDEXACX"
    @click="handleCreate"
  >
    <template #emptyState>
      <InboxPageEmptyState @click="handleCreate" />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <InboxCard
          v-for="aidexacxInbox in aidexacxInboxes"
          :id="aidexacxInbox.id"
          :key="aidexacxInbox.id"
          :inbox="aidexacxInbox"
          @action="handleAction"
        />
      </div>
    </template>

    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{
        assistantId: assistantId,
        inboxId: selectedInbox.id,
      }"
      type="Inboxes"
    />

    <ConnectInboxDialog
      v-if="dialogType"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      :type="dialogType"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
